<script setup lang="ts">
const props = defineProps<{ error: NuxtError & { data?: Record<string, any>; page?: string } }>();

const isVideosResults404 = computed(() => {
  const page = (props.error?.data as any)?.page ?? props.error?.page;
  return Number(props.error?.statusCode) === 404 && page === 'videos-results';
});

useHead(() => {
  if(props.error && props.error.statusCode !== 200) {
    return { meta: [{ name: 'robots', content: 'noindex, nofollow' }] }
  }
  return {}
})

const WadHero = defineAsyncComponent(() =>
    import('~/components/common/wadHeroSection.vue')
);

const rc = useRuntimeConfig();
const base_url = computed(
    () => (rc.public.wadPlatformBaseUrl as string) || ''
)
const videosUrl = `${base_url.value}/en/videos`;
</script>
<template>
  <NuxtLayout>
    <div>
      <!-- Special case: videos-results 404 -->
      <div v-if="isVideosResults404">
        <div class="wad4-error-page">
          <div class="wad4-error-page-header">
            <h1 class="wad4-typo__title">
              Sorry,<br>
              couldn't find any matching videos this time!
            </h1>
            <div class="wad4-typo__sub-title">
              <div>
                <a
                    class="wad4-error-page__underlined-link"
                    :href="videosUrl"
                >
                  Go back to the Watch page
                </a>
                and filter for other skills, topics or even speakers and events.
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Generic error hero variants -->
      <div v-else class="wad4-error-page">
        <WadHero
            v-if="error?.statusCode === 404"
            :imageName="'error-404'"
            :title="'Whoops! That page is gone.'"
            :subtitle="'We can’t find the page you’re looking for.'"
            :isError="true"
        />
        <WadHero
            v-else-if="error?.statusCode === 403"
            :imageName="'error-403'"
            :title="'Access denied.'"
            :subtitle="'Request understood but access denied! If you think this is in error please contact support.'"
            :isError="true"
        />
        <WadHero
            v-else
            :imageName="'error-500'"
            :title="'Whoops! Something just went wrong.'"
            :subtitle="'An error occured while processing your request. Please try again later.'"
            :isError="true"
        />
      </div>
    </div>
  </NuxtLayout>
</template>

<style lang="scss">
.wad4-error-page {
  background: $wad4-c-background-bg-primary;
  padding: $wad4-viewport-section-spacing  0;

  min-height: calc(100vh - (2 * var(--wad4-viewport-section-padding)) - 288px);

  @include mqBreakPoint(0, 767px) {
    padding-top: 0;
  }

  &-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__underlined-link {
    text-decoration: underline !important;
    color: $wad4-c-text-text-brand-tertiery;
  }
}
</style>
